import { useMemo, useState } from "react";
import { encode } from "uqr";
import { Button, Icon, Modal, Text } from "@app/ui";
import { downloadPassportQr } from "../api/dpp.js";
import { ErrorAlert } from "../errors/index.js";
import { useT } from "../i18n.js";
import type { Passport } from "../types.js";

/**
 * Inline QR rendering for list thumbnails / previews. Mirrors the API's
 * renderer (apps/api/src/lib/qr.ts): same `uqr` encoder, same single-path SVG,
 * explicit white background so it scans on dark theme too. The on-screen QR
 * encodes the /p/ public URL; the printable label (which may target /r/ for
 * GS1 identifiers) still comes from the API via the download button.
 */
export function QrSvg({ text, size, className }: { text: string; size: number; className?: string }) {
  const { size: modules, d } = useMemo(() => {
    const qr = encode(text, { ecc: "M", border: 2 });
    let path = "";
    for (let y = 0; y < qr.size; y++) {
      for (let x = 0; x < qr.size; x++) {
        if (qr.data[y]?.[x] === true) path += `M${x} ${y}h1v1h-1z`;
      }
    }
    return { size: qr.size, d: path };
  }, [text]);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={`0 0 ${modules} ${modules}`}
      shapeRendering="crispEdges"
      aria-hidden
      className={className}
    >
      <rect width={modules} height={modules} fill="#ffffff" />
      <path d={d} fill="#000000" />
    </svg>
  );
}

/**
 * List-cell QR: a small thumbnail for published passports that opens a
 * scannable large QR + label download in a modal. Draft/archived passports
 * have no public target (the API refuses their QR too), so they render an
 * em-dash like other empty cells.
 */
export function PassportQrCell({
  passport,
  orgId,
  label,
}: {
  passport: Passport;
  orgId: string;
  /** Accessible name + modal title, e.g. the product name. */
  label: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (passport.status !== "published") {
    return <span className="text-muted-foreground">—</span>;
  }

  const publicUrl = `${window.location.origin}/p/${encodeURIComponent(passport.publicSlug)}`;

  const download = async () => {
    setError(null);
    setDownloading(true);
    try {
      const blob = await downloadPassportQr(orgId, passport.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Must end in .svg so the OS opens it with an image viewer; mirrors the
      // API's own content-disposition filename.
      a.download = `passport-${passport.publicSlug}.qr.svg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    // The cell sits inside a clickable row. Radix portals re-bubble events
    // through the REACT tree (not the DOM), so clicks inside the dialog would
    // still reach the row's onRowClick and navigate away — this wrapper stops
    // everything (thumbnail + dialog) from leaking into the row.
    <span style={{ display: "contents" }} onClick={(e) => e.stopPropagation()}>
      {/* A real nested button: own hit target + focus ring inside the row.
          @app/ui's <Button> is control chrome and would fight the plain
          thumbnail look. */}
      {/* eslint-disable-next-line no-restricted-syntax */}
      <button
        type="button"
        aria-label={`${t("passports.qrShow")}: ${label}`}
        title={t("passports.qrShow")}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
        className="block rounded-md border border-border p-0.5 transition-colors hover:border-primary/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <QrSvg text={publicUrl} size={36} className="block rounded-[4px]" />
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={label}
        width={360}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t("common.close")}</Button>
            <Button
              variant="primary"
              icon={<Icon name="download" size={14} />}
              loading={downloading}
              onClick={() => void download()}
            >
              {t("passport.qr")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col items-center gap-3">
          {error != null && <ErrorAlert error={error} onRetry={() => void download()} />}
          <QrSvg text={publicUrl} size={224} className="rounded-lg border border-border" />
          <Text small secondary>
            {t("passports.qrHint")}
          </Text>
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="max-w-full truncate font-mono text-xs text-primary underline-offset-2 hover:underline"
          >
            {publicUrl}
          </a>
        </div>
      </Modal>
    </span>
  );
}
